import React, { useState, useEffect } from "react";
import { Box, Button, Typography, Divider } from "@mui/material";
import { Upload } from "lucide-react";
import { FileDropzone } from "./FileDropzone.tsx";
import FileValidation from "./FileValidation.tsx";
import { useFileDropzone } from "../../../hooks/useFileDropzone.ts";

interface AlertConfig {
  open: boolean;
  type: "loading" | "success" | "error" | "confirm";
  message: string;
  title?: string;
  onConfirm?: () => Promise<void>;
}

interface FileUploadSectionProps {
  currentPath: string[];
  selectedFiles: File[];
  setSelectedFiles: (files: File[]) => void;
  fileNameError: string;
  setFileNameError: (error: string) => void;
  showFileUploadSuccessMessage: boolean;
  isDarkMode: boolean;
  isUploading: boolean;
  textConfig?: any;
  onUploadFiles: () => Promise<void>;
  onFileSelection: (files: File[]) => void;
  setAlertConfig: (config: AlertConfig) => void;
  truncateFileName: (fileName: string, maxLength?: number) => string;
}

const FileUploadSection = ({
  currentPath,
  selectedFiles,
  setSelectedFiles,
  fileNameError,
  setFileNameError,
  showFileUploadSuccessMessage,
  isDarkMode,
  isUploading,
  textConfig: externalTextConfig,
  onUploadFiles,
  onFileSelection,
  setAlertConfig,
  truncateFileName
}: FileUploadSectionProps) => {

  const [internalTextConfig, setInternalTextConfig] = useState<any>({});

  const fetchTextConfig = async () => {
    try {
      const response = await fetch("/text.json");
      const data = await response.json();
      setInternalTextConfig(data);
    } catch (error) {
      console.error("Error loading text config:", error);
    }
  };

  useEffect(() => {
    fetchTextConfig();
  }, []);

  // Use external textConfig if provided, otherwise use internal
  const textConfig = externalTextConfig || internalTextConfig;

  const {
    getRootProps,
    getInputProps,
    isDragActive,
  } = useFileDropzone({
    onFileSelect: onFileSelection,
    setFileNameError,
    setAlertConfig,
    disabled: currentPath.length === 0,
  });

  const handleRemoveFile = (index: number) => {
    setSelectedFiles(selectedFiles.filter((_, i) => i !== index));
  };

  if (currentPath.length === 0) {
    return null;
  }

  return (
    <>
      <Divider sx={{ my: 2 }} />
      
      <Typography
        variant="subtitle1"
        fontWeight="bold"
        color={isDarkMode ? "#fff" : "#374151"}
        mb={2}
      >
        {textConfig.FILE_UPLOAD_SECTION_TITLE}
      </Typography>

      <FileDropzone
        getRootProps={getRootProps}
        getInputProps={getInputProps}
        isDragActive={isDragActive}
        selectedFiles={selectedFiles}
        onRemoveFile={handleRemoveFile}
        textConfig={textConfig}
        isDarkMode={isDarkMode}
        truncateFileName={truncateFileName}
      />

      {selectedFiles.length > 0 && (
        <Box display="flex" mt={2}>
          <Button
            variant="outlined"
            startIcon={<Upload size={16} />}
            onClick={onUploadFiles}
            disabled={isUploading || selectedFiles.length === 0}
            sx={{
              backgroundColor: "#27272A",
              borderColor: "#22C55E",
              borderWidth: "2px",
              color: "#FFFFFF",
              fontWeight: "bold",
              boxShadow: "0 4px 6px -1px rgba(34, 197, 94, 0.2)",
              "&:hover": {
                backgroundColor: "#363639",
                borderColor: "#16A34A",
                transform: "translateY(-2px)",
                boxShadow: "0 6px 10px -1px rgba(34, 197, 94, 0.3)",
              },
              "&:disabled": {
                backgroundColor: "#1F1F1F",
                borderColor: "#383838",
                color: "#666666",
                transform: "none",
              },
              animation: selectedFiles.length > 0 ? "pulse 2s infinite" : "none",
              "@keyframes pulse": {
                "0%": {
                  boxShadow: "0 0 0 0 rgba(34, 197, 94, 0.4)",
                },
                "70%": {
                  boxShadow: "0 0 0 10px rgba(34, 197, 94, 0)",
                },
                "100%": {
                  boxShadow: "0 0 0 0 rgba(34, 197, 94, 0)",
                },
              },
              transition: "all 0.3s ease",
            }}
          >
            {isUploading ? textConfig.FILE_UPLOAD_SECTION_UPLOADING : textConfig.FILE_UPLOAD_SECTION_UPLOAD_BUTTON}
          </Button>
        </Box>
      )}

      <FileValidation
        files={[]}
        isDarkMode={isDarkMode}
        showFileUploadSuccessMessage={showFileUploadSuccessMessage}
        onRemoveFile={() => {}}
        truncateFileName={truncateFileName}
      />
    </>
  );
};

export default FileUploadSection;